import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormControlOptions,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { FareSubType, FareType, JourneyFare } from '../../model/journey';
import { ValueLabelItem } from '../../../model/value-label-item';
import { ButtonGroupItem, hasActiveButtonItem } from '../../../model/button-group-item';
import { partition } from '../../../util/arrayUtils';
import { getPropertyByKey } from '../../../util/utils';
import { FeatureFlags } from '../../../core/util/resources';
import { combineLatest, Subscription } from 'rxjs';
import { ConfigurationService } from '../../../service/configuration/configuration.service';
import {
  MAX_LENGTH_STRING_VALIDATION_MESSAGES,
  MIN_LENGTH_STRING_VALIDATION_MESSAGES,
  REQUIRED_MSG,
  SECTION_VALIDATION_MESSAGES
} from '../../../service/model/common/validation-messages';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ButtonGroupComponent } from '../../../components/button-group/button-group.component';
import { LookupSelectComponent } from '../../../components/lookup-select/lookup-select.component';
import { ValidationErrorMessageComponent } from '../../../components/validation-error-message/validation-error-message.component';

enum FareControl {
  fareTypes = 'fareTypes',
  subFareTypes = 'subFareTypes',
  fareTypeNames = 'fareTypeNames',
  fareScope = 'fareScope',
  fareBasis = 'fareBasis'
}

enum FareButton {
  fareType = 'fareType',
  fareBasis = 'fareBasis'
}

@Component({
  selector: 'ama-ng-upp-fare-content',
  templateUrl: './fare-content.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonGroupComponent,
    ValidationErrorMessageComponent,
    LookupSelectComponent,
    NgbTooltip
  ]
})
export class FareContentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fareFormGroup!: UntypedFormGroup;
  @Input() fareScope?: JourneyFare;
  @Input() readonly = false;

  private readonly subscription: Subscription = new Subscription();

  fareItems: FareItem[] = [];

  fareScopeButtonGroupItems: ButtonGroupItem[] = [];
  fareScopeButtonGroupMap: { [key: string]: ButtonGroupItem | undefined } = {};

  flightsFareTypeEnabled = false;
  flightsUiChangesEnabled = false;

  private readonly buttonToFormConfig = {
    [FareButton.fareType]: [
      () => this.createFareControl(FareControl.fareTypes),
      () => this.createFareControl(FareControl.subFareTypes),
      () => this.createFareControl(FareControl.fareTypeNames, [Validators.required])
    ],
    [FareControl.fareBasis]: [() => this.createFareBasisControl()]
  };

  readonly allFareTypes: string[] = Object.values(FareType);
  fareBasisValidationMessages = {
    ...MIN_LENGTH_STRING_VALIDATION_MESSAGES,
    ...MAX_LENGTH_STRING_VALIDATION_MESSAGES,
    ...REQUIRED_MSG
  };
  readonly SECTION_VALIDATION_MESSAGES = SECTION_VALIDATION_MESSAGES;

  fareTypesMapV2 = {
    [FareType.RP]: {
      value: FareType.RP,
      disabled: false,
      label: $localize`:@upp.flights.details.fareType.RP:RP - PUBLISHED FARES`
    },
    [FareType.RW]: {
      value: FareType.RW,
      disabled: false,
      label: $localize`:@upp.flights.details.fareType.RW:RW - CORPORATE FARES`
    },
    [FareType.RU]: {
      value: FareType.RU,
      disabled: false,
      label: $localize`:@upp.flights.details.fareType.RU:RU - PRIVATE/NEGOTIATED FARES`
    },
    [FareSubType.TO]: {
      value: FareSubType.TO,
      disabled: false,
      label: $localize`:@upp.flights.details.fareSubType.TO:TO - TOUR OPERATOR`
    },
    [FareSubType.CS]: {
      value: FareSubType.CS,
      disabled: false,
      label: $localize`:@upp.flights.details.fareSubType.CS:CS - CONSOLIDATOR`
    },
    [FareSubType.VF]: {
      value: FareSubType.VF,
      disabled: false,
      label: $localize`:@upp.flights.details.fareSubType.VF:VF - VISIT FRIEND`
    }
  };

  fareTypesMap = {
    [FareType.RP]: {
      value: FareType.RP,
      disabled: false,
      label: $localize`:@upp.flights.details.fareType.RP:RP - PUBLISHED FARES`
    },
    [FareType.RW]: {
      value: FareType.RW,
      disabled: false,
      label: $localize`:@upp.flights.details.fareType.RW:RW - CORPORATE FARES`
    },
    [FareType.RU]: {
      value: FareType.RU,
      disabled: false,
      label: $localize`:@upp.flights.details.fareType.RU:RU - PRIVATE/NEGOTIATED FARES`
    }
  };

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly configurationService: ConfigurationService
  ) {}

  get fareTypeNames(): AbstractControl | null {
    return this.fareFormGroup.get(FareControl.fareTypeNames);
  }

  get fareBasisContainsControl(): FormControl {
    return this.fareFormGroup.get(FareControl.fareBasis)?.get('contains') as FormControl;
  }

  get fareScopeControl(): AbstractControl | null {
    return this.fareFormGroup.get(FareControl.fareScope);
  }

  groupValueFn = (groupName: string) => getPropertyByKey(this.fareTypesMap, groupName);

  ngOnInit(): void {
    // Wait for both feature flags before initializing
    this.subscription.add(
      combineLatest([
        this.configurationService.getParameter$(FeatureFlags.flightsFareType),
        this.configurationService.getParameter$(FeatureFlags.flightsUiChanges)
      ]).subscribe(([flightsFareType, flightsUiChanges]) => {
        const previousUiChangesValue = this.flightsUiChangesEnabled;

        this.flightsFareTypeEnabled = flightsFareType ?? false;
        this.flightsUiChangesEnabled = flightsUiChanges ?? false;

        this.initializeFareItems();
        if (previousUiChangesValue !== this.flightsUiChangesEnabled && this.fareScopeButtonGroupItems.length > 0) {
          this.regenerateButtonGroupItems();
        }
      })
    );
  }

  private initializeFareItems(): void {
    if (this.flightsFareTypeEnabled) {
      this.fareItems = [
        this.fareTypesMapV2[FareType.RP],
        this.fareTypesMapV2[FareType.RW],
        this.fareTypesMapV2[FareType.RU],
        this.fareTypesMapV2[FareSubType.TO],
        this.fareTypesMapV2[FareSubType.CS],
        this.fareTypesMapV2[FareSubType.VF]
      ];
    } else {
      this.fareItems = [
        this.fareTypesMap[FareType.RP],
        {
          value: FareSubType.TO,
          label: $localize`:@upp.flights.details.fareSubType.TO:TO - TOUR OPERATOR`,
          parent: FareType.RU
        },
        {
          value: FareSubType.CS,
          label: $localize`:@upp.flights.details.fareSubType.CS:CS - CONSOLIDATOR`,
          parent: FareType.RU
        },
        {
          value: FareSubType.VF,
          label: $localize`:@upp.flights.details.fareSubType.VF:VF - VISIT FRIEND`,
          parent: FareType.RU
        },
        this.fareTypesMap[FareType.RW]
      ];
    }

    this.fareTypeNamesChanged(this.fareTypeNames?.value);
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.regenerateButtonGroupItems();
  }

  private regenerateButtonGroupItems(): void {
    this.fareFormGroup.setControl(FareControl.fareScope, this.formBuilder.control(false, [Validators.requiredTrue]));
    this.fareScopeButtonGroupItems = this.generateFareScopeButtonGroupItems();
    this.fareScopeButtonGroupMap = this.fareScopeButtonGroupItems.reduce((acc: any, item: any) => {
      acc[item.name] = item;
      this.onToggle(item);
      return acc;
    }, {});
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  fareTypeNamesChanged(newValue: string[]): void {
    const [fareTypes, subFareTypes] = partition<string>(newValue ?? [], (value) => this.allFareTypes.includes(value));

    if (this.flightsFareTypeEnabled) {
      if (fareTypes.includes(FareType.RU)) {
        this.fareItems = this.fareItems.map((x) => ({
          ...x,
          disabled: x.value === FareSubType.CS || x.value === FareSubType.TO || x.value === FareSubType.VF
        }));
      } else if (
        subFareTypes.includes(FareSubType.CS) ||
        subFareTypes.includes(FareSubType.TO) ||
        subFareTypes.includes(FareSubType.VF)
      ) {
        this.fareItems = this.fareItems.map((x) => ({
          ...x,
          disabled: x.value === FareType.RU
        }));
      } else {
        this.fareItems = this.fareItems.map((x) => ({
          ...x,
          disabled: false
        }));
      }
    }

    if (subFareTypes.length > 0) {
      fareTypes.push(FareType.RU);
    }

    this.fareFormGroup.patchValue({
      fareTypes,
      subFareTypes
    });
  }

  onToggle(item: ButtonGroupItem) {
    item.active ? this.configureButtonForms(item) : this.fareFormGroup.removeControl(item.name);

    this.fareScopeControl?.markAsTouched();
    this.fareScopeControl?.setValue(hasActiveButtonItem(this.fareScopeButtonGroupItems));
  }

  private configureButtonForms(item: ButtonGroupItem): void {
    this.buttonToFormConfig[item.name as FareButton].forEach((configFn: () => void) => configFn());
  }

  private createFareControl(controlName: keyof JourneyFare, validatorOrOpts?: ValidatorOrOpts): void {
    this.fareFormGroup.setControl(
      controlName,
      this.formBuilder.control({ value: this.fareScope?.[controlName], disabled: this.readonly }, validatorOrOpts)
    );
  }

  private generateFareScopeButtonGroupItems(): ButtonGroupItem[] {
    const items: ButtonGroupItem[] = [
      {
        name: FareButton.fareType,
        active: true,
        disabled: this.readonly,
        title: $localize`:@@upp.global.criteria.fareType.label:Fare type`,
        canToggle: () => false
      }
    ];
    if (!this.flightsUiChangesEnabled) {
      items.push({
        name: FareButton.fareBasis,
        active: !!this.fareScope?.fareBasis,
        disabled: this.readonly,
        title: $localize`:@@upp.global.criteria.fareBasis.label:Fare basis`
      });
    }

    return items;
  }

  private createFareBasisControl(): void {
    this.fareFormGroup.setControl(
      FareControl.fareBasis,
      this.formBuilder.group({
        contains: [
          { value: this.fareScope?.fareBasis?.contains, disabled: this.readonly },
          [Validators.required, Validators.minLength(3), Validators.maxLength(14)]
        ]
      })
    );
  }
}

interface FareItem extends ValueLabelItem<string> {
  parent?: any;
  disabled?: boolean;
}

type ValidatorOrOpts = ValidatorFn | ValidatorFn[] | FormControlOptions | null;
